// Independent of A-F CCMR — do not import Ccmr/* or write ccmrIndicators*.
//
// CCMR Outcomes Bonus (HB3 Funding Forecast) screen. Renders server-computed funding
// figures only — it never recomputes readiness or thresholds. Walled off from the A-F
// CCMR pipeline: its own AssessmentType, validator, parser, aggregator, node, and read
// endpoint.
import * as React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { saveAs } from 'file-saver';
import { withStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Cancel';
import CloseIcon from '@material-ui/icons/Close';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Typography, Chip, Snackbar, SnackbarContent, IconButton, Button } from '@material-ui/core';
import styles from './OutcomesBonus.styles';
import { LoadingCircle, Dropzone, TitleBackButton, ExportCSVButton, makeCsv } from '../common';
import {
  fyLabel,
  spedRate,
  formatDollars,
  bandsLabel,
  COHORT_LABELS,
  COHORT_ORDER,
  NEAR_QUALIFIER_CSV_COLUMNS,
  shapeNearQualifierForCsv,
} from './+store/outcomesBonus.selectors';

// The TEA CCMR Outcomes Bonus report is an xlsx. Matches the Assessment Manager
// dropzone's accepted set for the xlsx type.
const UPLOAD_ACCEPT = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', '.xlsx'];

class OutcomesBonus extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.any.isRequired,
    profile: PropTypes.object,
    gradYear: PropTypes.string,
    init: PropTypes.func.isRequired,
    getOutcomesBonus: PropTypes.func.isRequired,
    uploadFile: PropTypes.func.isRequired,
    outcomesBonus: PropTypes.object,
    schoolList: PropTypes.object,
    loading: PropTypes.bool,
    loadingMsg: PropTypes.string,
    upload: PropTypes.object,
  };

  state = {
    isLoadedBefore: false,
    toast: null,
    lastUploadSeq: 0,
    rosterNoteDismissed: false,
    exporting: false,
    // The graduating class the upload will be filed under. The TEA OB file is named by
    // district (no year), so the user picks it. Default to the most recent class with final
    // counts (~2-year funding lag).
    selectedYear: String(new Date().getFullYear() - 2),
  };

  componentDidMount() {
    // The upload redux slice persists across navigation, so a previously-completed
    // upload still carries a non-zero seq + result. Baseline lastUploadSeq to the
    // current store seq on mount so we only toast for uploads done THIS visit.
    const { upload } = this.props;
    this.setState({ lastUploadSeq: (upload && upload.seq) || 0 });
    this.shouldInit();
  }

  componentDidUpdate() {
    this.shouldInit();
    this.handleUploadResult();
  }

  shouldInit = () => {
    const { profile, init, getOutcomesBonus, gradYear } = this.props;
    const { isLoadedBefore } = this.state;
    if (profile && !isLoadedBefore) {
      this.setState({ isLoadedBefore: true });
      init(profile);
      getOutcomesBonus(gradYear);
    }
  };

  // Fire a toast whenever the upload lifecycle settles (seq bumps once per finished
  // upload, success or error). Driven by redux so it survives the node refresh.
  handleUploadResult = () => {
    const { upload } = this.props;
    if (!upload || upload.seq === this.state.lastUploadSeq) return;
    const next = { lastUploadSeq: upload.seq };
    if (upload.error) {
      this.setState(Object.assign(next, { toast: { isError: true, text: upload.error } }));
    } else if (upload.result) {
      const { totalDollars, gradYear } = upload.result;
      this.setState(
        Object.assign(next, {
          toast: {
            isError: false,
            text: `Estimated ${formatDollars(totalDollars)} for the Class of ${gradYear}, payable in ${fyLabel(
              gradYear
            )}.`,
          },
        })
      );
    } else {
      this.setState(next);
    }
  };

  handleDrop = (accepted) => {
    const { uploadFile, profile, upload } = this.props;
    if (upload && upload.uploading) return;
    if (accepted && accepted.length) {
      uploadFile(profile, accepted[0], this.state.selectedYear);
    }
  };

  handleYearChange = (event) => this.setState({ selectedYear: event.target.value });

  closeToast = () => this.setState({ toast: null });

  dismissRosterNote = () => this.setState({ rosterNoteDismissed: true });

  getAggregate = () => {
    const { outcomesBonus } = this.props;
    return (outcomesBonus && outcomesBonus.aggregate) || null;
  };

  getNearQualifiers = () => {
    const { outcomesBonus } = this.props;
    return (outcomesBonus && outcomesBonus.nearQualifiers) || [];
  };

  getTrend = () => {
    const { outcomesBonus } = this.props;
    const trend = (outcomesBonus && outcomesBonus.trend) || [];
    // Stable ascending order by grad year for the strip.
    return trend.slice().sort((a, b) => Number(a.gradYear) - Number(b.gradYear));
  };

  getCampusName = (campusNumber) => {
    const { schoolList } = this.props;
    if (schoolList && campusNumber && schoolList[campusNumber]) {
      return schoolList[campusNumber].name;
    }
    return campusNumber || '';
  };

  exportCsv = async () => {
    const rows = this.getNearQualifiers().map((row) =>
      shapeNearQualifierForCsv(Object.assign({}, row, { campusName: this.getCampusName(row.campus) || row.campusName }))
    );
    this.setState({ exporting: true });
    try {
      const output = await makeCsv(rows, NEAR_QUALIFIER_CSV_COLUMNS);
      const blob = new Blob([output], { type: 'text/csv;charset=utf-8;' });
      const aggregate = this.getAggregate() || {};
      saveAs(blob, `CCMR-Outcomes-Bonus-Near-Qualifiers-${aggregate.gradYear || ''}.csv`);
    } finally {
      this.setState({ exporting: false });
    }
  };

  renderBanner = (aggregate) => {
    const { classes } = this.props;
    const isFinal = (aggregate.countsType || 'final') === 'final';
    const gradYear = aggregate.gradYear;
    const rate = aggregate.rates ? aggregate.rates.sped : spedRate(gradYear);
    return (
      <div className={classNames(classes.banner, isFinal ? classes.bannerFinal : classes.bannerEarly)}>
        <span>{`${isFinal ? 'FINAL COUNTS' : 'EARLY COUNTS'} - Class of ${gradYear}`}</span>
        <span>{`pays into ${fyLabel(gradYear)} Summary of Finances`}</span>
        <span>{`SpEd rate ${formatDollars(rate)} (Class of ${gradYear}${
          Number(gradYear) >= 2024 ? ', HB2' : ''
        })`}</span>
        {!isFinal && (
          <span className={classes.bannerNote}>
            THECB postsecondary enrollment not yet included; this estimate may rise at settle-up.
          </span>
        )}
      </div>
    );
  };

  renderHero = (aggregate) => {
    const { classes } = this.props;
    return (
      <div className={classes.hero}>
        <Typography className={classes.heroLabel}>Estimated CCMR Outcomes Bonus (district total)</Typography>
        <div className={classes.heroTotal}>{formatDollars(aggregate.totalDollars)}</div>
        <Typography className={classes.heroSub}>
          {`${aggregate.totalDistinctGraduates || 0} annual graduates - Class of ${aggregate.gradYear} - estimate`}
        </Typography>
      </div>
    );
  };

  renderCohortCard = (key, aggregate) => {
    const { classes } = this.props;
    const band = (aggregate.byBand && aggregate.byBand[key]) || {
      grads: 0,
      qualifiers: 0,
      thresholdCount: 0,
      fundedCount: 0,
      rate: 0,
      dollars: 0,
    };
    const meta = COHORT_LABELS[key];
    return (
      <div key={key} className={classes.cohortCard}>
        <Typography className={classes.cohortTitle}>{meta.title}</Typography>
        <Typography className={classes.cohortRate}>{`${formatDollars(band.rate)} / grad`}</Typography>
        <div className={classes.cohortRow}>
          <span>Grads</span>
          <span>{band.grads}</span>
        </div>
        <div className={classes.cohortRow}>
          <span>OB Met</span>
          <span>{band.qualifiers}</span>
        </div>
        <div className={classes.cohortRow}>
          <span>{`Threshold (${meta.thresholdPct}%)`}</span>
          <span>{band.thresholdCount}</span>
        </div>
        <div className={classes.cohortRow}>
          <span>Above threshold</span>
          <span>{`= ${band.fundedCount}`}</span>
        </div>
        <div className={classes.cohortDollars}>{formatDollars(band.dollars)}</div>
      </div>
    );
  };

  // Grounded AI caption — a short, plain-language read of the aggregate numbers, generated at
  // upload from aggregate counts ONLY (no student data) with a fail-open Anthropic -> OpenAI
  // failover. Renders nothing when absent (no keys / both providers down), so the deterministic
  // panel below is always the source of truth.
  renderAiCaption = (aggregate) => {
    const caption = aggregate.aiCaption;
    if (!caption) return null;
    return (
      <div
        style={{
          border: '1px solid #d4e3f0',
          borderLeft: '4px solid #4a90d9',
          borderRadius: 6,
          padding: '12px 16px',
          margin: '16px 0',
          background: '#f5f9fd',
        }}
      >
        <Typography style={{ fontSize: 15, lineHeight: 1.5 }}>{caption}</Typography>
        <Typography style={{ fontSize: 11, color: '#8a8a8a', marginTop: 8, fontStyle: 'italic' }}>
          {aggregate.aiCaptionDisclaimer ||
            'This summary was generated from aggregate counts only. No individual student data was sent to or retained by the AI service.'}
        </Typography>
      </div>
    );
  };

  // Deterministic "why this number" panel — aggregate counts only (no student data). Surfaces
  // where the class is losing the bonus. The grounded AI caption above is fed ONLY these
  // aggregates, with a "no individual student data sent/retained" disclaimer.
  renderBreakdown = (aggregate) => {
    const { classes } = this.props;
    const b = aggregate.breakdown;
    if (!b) return null;
    const spedRateVal = (aggregate.rates && aggregate.rates.sped) || spedRate(aggregate.gradYear);
    const insights = [];
    insights.push(
      `${b.obMet} of ${b.totalGrads} graduates (${b.obMetRate}%) met the Outcomes Bonus criteria, and the bonus pays only above each cohort's threshold.`
    );
    insights.push(
      `TSI college readiness is the gate: only ${b.metBothAssess} cleared TSI in BOTH RLA and Math, which the bonus requires for the college route and (with a certificate) the career route.`
    );
    if (b.enrolledNotTsiReady > 0) {
      insights.push(
        `${b.enrolledNotTsiReady} graduates enrolled at a college but did not clear TSI in both subjects, so they do not count toward the college bonus.`
      );
    }
    if (b.certNotTsiReady > 0) {
      insights.push(
        `${b.certNotTsiReady} earned an industry-based or Level I/II certificate but did not clear TSI, so they do not count toward the career bonus.`
      );
    }
    if (b.spedGrads > 0) {
      insights.push(
        `${b.spedGrads} Special Education graduates, ${
          b.spedMet
        } met. SpEd has no threshold, so every qualifier is ${formatDollars(spedRateVal)} with nothing subtracted.`
      );
    }
    if (b.oneIndicatorShort > 0) {
      insights.push(
        `${b.oneIndicatorShort} graduates are one indicator short of qualifying. See "Dollars within reach" below.`
      );
    }
    return (
      <div
        className={classes.breakdownPanel}
        style={{ border: '1px solid #e0e0e0', borderRadius: 6, padding: 16, margin: '16px 0', background: '#fafafa' }}
      >
        <Typography className={classes.sectionTitle}>Why this number: where the bonus is being lost</Typography>
        <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
          {insights.map((text) => (
            <li key={text} style={{ marginBottom: 6, fontSize: 14, lineHeight: 1.4 }}>
              {text}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  renderTrend = (trend) => {
    const { classes } = this.props;
    if (!trend.length) return null;
    const max = Math.max(1, ...trend.map((t) => Number(t.totalDollars) || 0));
    return (
      <div className={classes.trendStrip}>
        <Typography className={classes.sectionTitle}>Year over Year (district OB total)</Typography>
        {trend.map((t) => {
          const pct = ((Number(t.totalDollars) || 0) / max) * 100;
          const isFinal = (t.countsType || 'final') === 'final';
          return (
            <div key={t.gradYear} className={classes.trendRow}>
              <span>{`'${String(t.gradYear).slice(-2)}${isFinal ? 'F' : 'E'}`}</span>
              <div className={classes.trendBarWrap}>
                <div className={classes.trendBar} style={{ width: `${pct}%` }} />
              </div>
              <span>{formatDollars(t.totalDollars)}</span>
            </div>
          );
        })}
        <Typography className={classes.helpText}>F = final (THECB included; settle-up done). E = early.</Typography>
      </div>
    );
  };

  renderWorklist = (nearQualifiers) => {
    const { classes } = this.props;
    return (
      <div className={classes.worklist}>
        <div className={classes.worklistHead}>
          <Typography className={classes.sectionTitle}>Dollars within reach (near-qualifiers)</Typography>
        </div>
        {nearQualifiers.length === 0 && (
          <Typography className={classes.helpText}>
            No near-qualifiers in view. Every graduate either already met CCMR OB criteria or has no actionable path
            this class.
          </Typography>
        )}
        {nearQualifiers.map((row, index) => (
          <div key={`${row.localId || row.studentName || 'row'}-${index}`} className={classes.worklistRow}>
            <span className={classes.worklistName}>{row.studentName || row.localId || 'Student'}</span>
            <span>{this.getCampusName(row.campus) || row.campusName || ''}</span>
            <Chip className={classNames(classes.chip, classes.missingChip)} label={bandsLabel(row.bands) || 'Cohort'} />
            {(row.missing || []).map((m) => (
              <Chip key={m} className={classNames(classes.chip, classes.missingChip)} label={m} />
            ))}
            {row.stacked9000 && (
              <Chip className={classNames(classes.chip, classes.stackedChip)} label="Stacks $9,000" />
            )}
            <span className={classes.marginalDollars}>{`+${formatDollars(row.marginalDollars)}`}</span>
          </div>
        ))}
      </div>
    );
  };

  renderUploadZone = () => {
    const { classes, upload } = this.props;
    const { selectedYear } = this.state;
    const busy = !!(upload && upload.uploading);
    const currentYear = new Date().getFullYear();
    const years = [];
    // HB3 CCMR Outcomes Bonus applies to graduates from the Class of 2020 onward.
    for (let y = currentYear; y >= 2020; y -= 1) {
      years.push(String(y));
    }
    return (
      <React.Fragment>
        <div className={classes.uploadControls} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <label htmlFor="ob-grad-year" style={{ fontWeight: 600, marginRight: 12 }}>
            Graduating class
          </label>
          <select
            id="ob-grad-year"
            value={selectedYear}
            disabled={busy}
            onChange={this.handleYearChange}
            style={{ padding: '6px 10px', fontSize: 14, borderRadius: 4, border: '1px solid #c4c4c4', marginRight: 12 }}
          >
            {years.map((y) => (
              <option key={y} value={y}>{`Class of ${y}`}</option>
            ))}
          </select>
          <span style={{ color: '#777', fontSize: 12 }}>
            TEA names these files by district, not year — pick the graduating class before importing.
          </span>
        </div>
        <Dropzone
          accept={UPLOAD_ACCEPT}
          multiple={false}
          disabled={busy}
          onDrop={this.handleDrop}
          className={classNames(classes.uploadZone, busy ? classes.uploadZoneBusy : null)}
          activeClassName={classes.uploadZoneActive}
        >
          {busy ? (
            <React.Fragment>
              <CircularProgress size={26} className={classes.uploadIcon} />
              <Typography className={classes.uploadTitle}>Uploading and Computing Funding ...</Typography>
              <Typography className={classes.uploadHint}>
                Parsing the file and aggregating the three cohorts to the district funding total.
              </Typography>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <CloudUploadIcon className={classes.uploadIcon} />
              <Typography className={classes.uploadTitle}>Import a TEA CCMR Outcomes Bonus file</Typography>
              <Typography className={classes.uploadHint}>
                Upload your &quot;CCMR_Outcomes_Bonus_&lt;district#&gt;.xlsx&quot; export here.
              </Typography>
            </React.Fragment>
          )}
        </Dropzone>
      </React.Fragment>
    );
  };

  renderToast = () => {
    const { classes } = this.props;
    const { toast } = this.state;
    if (!toast) return null;
    const Icon = toast.isError ? ErrorIcon : CheckCircleIcon;
    return (
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open
        autoHideDuration={6000}
        onClose={this.closeToast}
      >
        <SnackbarContent
          className={classes[toast.isError ? 'error' : 'success']}
          message={
            <span className={classes.message}>
              <Icon className={classes.icon} />
              {toast.text}
            </span>
          }
          action={[
            <IconButton key="close" aria-label="Close" color="inherit" onClick={this.closeToast}>
              <CloseIcon className={classes.icon} />
            </IconButton>,
          ]}
        />
      </Snackbar>
    );
  };

  render() {
    const { classes, profile, loading, loadingMsg } = this.props;
    const { rosterNoteDismissed, exporting } = this.state;

    if (!profile || loading) {
      return (
        <div className={classes.content}>
          <LoadingCircle classes={classes} msg={loadingMsg} />
        </div>
      );
    }

    const aggregate = this.getAggregate();
    const nearQualifiers = this.getNearQualifiers();
    const trend = this.getTrend();
    const isEmpty = !aggregate;

    return (
      <div className={classes.root}>
        {this.renderToast()}

        <div className={classes.headerRow}>
          <TitleBackButton title="CCMR Outcomes Bonus (HB3 Funding Forecast)" />
          {!isEmpty && <ExportCSVButton title="Export CSV" onClick={this.exportCsv} loading={exporting} />}
        </div>

        {this.renderUploadZone()}

        {isEmpty && (
          <div className={classes.helpText}>
            No CCMR Outcomes Bonus file has been imported yet. Drop the TEA
            &quot;CCMR_Outcomes_Bonus_&lt;district#&gt;.xlsx&quot; file in the box above to compute this graduating
            class&apos;s HB3 funding estimate.
          </div>
        )}

        {!isEmpty && (
          <React.Fragment>
            {this.renderBanner(aggregate)}

            {!rosterNoteDismissed && (
              <div className={classes.rosterNote}>
                <InfoOutlinedIcon style={{ fontSize: 18 }} />
                <span>
                  {`OB file contains ${aggregate.totalDistinctGraduates || 0} annual-graduate rows for the Class of ${
                    aggregate.gradYear
                  }. Roster 12th-grade completers may differ from TEA annual graduates (summer/August grads, continuers); not an error.`}
                </span>
                <Button size="small" color="primary" onClick={this.dismissRosterNote}>
                  Dismiss
                </Button>
              </div>
            )}

            {this.renderHero(aggregate)}

            <div className={classes.cohortCards}>
              {COHORT_ORDER.map((key) => this.renderCohortCard(key, aggregate))}
            </div>

            <Typography className={classes.spedNote}>
              {`${(aggregate.byBand && aggregate.byBand.eco && aggregate.byBand.eco.grads) ||
                0} eco + ${(aggregate.byBand && aggregate.byBand.nonEco && aggregate.byBand.nonEco.grads) ||
                0} non-eco = ${aggregate.totalDistinctGraduates || 0} total. The ${(aggregate.byBand &&
                aggregate.byBand.sped &&
                aggregate.byBand.sped.grads) ||
                0} SpEd grads are a SUBSET of those graduates (also counted above); their dollars STACK on any eco/non-eco bonus.`}
            </Typography>

            {this.renderAiCaption(aggregate)}

            {this.renderBreakdown(aggregate)}

            <div className={classes.bottomGrid}>
              {this.renderTrend(trend)}
              {this.renderWorklist(nearQualifiers)}
            </div>

            <Typography className={classes.helpText}>
              Rounding: ThresholdCount = floor(pct x grads) (TEA-exact, validated). All figures are estimates and settle
              up at the Summary of Finances. Independent of A-F accountability.
            </Typography>
          </React.Fragment>
        )}
      </div>
    );
  }
}

export default withStyles(styles)(OutcomesBonus);
